import { useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, MapPin, Calendar, Tag } from "lucide-react";
import { getProjektById } from "@/data/projekte";
import { useLanguage } from "@/contexts/LanguageContext";
import { T } from "@/components/T";

function useReveal() {
  useEffect(() => {
    const els = document.querySelectorAll('.reveal');
    const observer = new IntersectionObserver(
      (entries) => entries.forEach(e => { if (e.isIntersecting) e.target.classList.add('visible'); }),
      { threshold: 0.06, rootMargin: '0px 0px -40px 0px' }
    );
    els.forEach(el => observer.observe(el));
    return () => els.forEach(el => observer.unobserve(el));
  });
}

export default function ProjektDetail() {
  const { id } = useParams();
  const projekt = getProjektById(id);
  const { lang } = useLanguage();
  useReveal();

  const d = (field) => (lang === 'it' && projekt?.it?.[field]) ? projekt.it[field] : projekt?.[field];

  if (!projekt) {
    return (
      <div className="pt-40 text-center">
        <p className="text-hb-muted"><T de="Projekt nicht gefunden." it="Progetto non trovato." /></p>
        <Link to="/projekte" className="btn-link mt-4 inline-block">
          <T de="Zurück zu Projekten" it="Torna ai Progetti" />
        </Link>
      </div>
    );
  }

  return (
    <div className="pt-20 md:pt-24">
      {/* Hero */}
      <section className="relative h-[60vh] md:h-[70vh] overflow-hidden" data-testid="projekt-hero">
        <img
          src={projekt.image}
          alt={d('title')}
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-hb-dark/80 via-hb-dark/20 to-transparent" />
        <div className="absolute bottom-0 left-0 right-0 container-hb pb-12">
          <Link
            to="/projekte"
            className="inline-flex items-center gap-2 text-white/60 hover:text-white text-xs tracking-widest uppercase font-semibold mb-6 transition-colors"
          >
            <ArrowLeft size={12} /> <T de="Projekte" it="Progetti" />
          </Link>
          <div className="flex flex-wrap gap-3 mb-4">
            <span className="bg-hb-wood/20 text-hb-wood text-[10px] tracking-widest uppercase font-semibold px-3 py-1">
              {projekt.kategorie}
            </span>
          </div>
          <h1
            data-testid="projekt-title"
            className="text-4xl sm:text-5xl font-manrope font-extrabold text-white"
          >
            {d('title')}
          </h1>
        </div>
      </section>

      {/* Meta */}
      <section className="bg-hb-off-white border-b border-hb-border py-6">
        <div className="container-hb">
          <div className="flex flex-wrap gap-8">
            <div className="flex items-center gap-2 text-sm text-hb-text">
              <MapPin size={14} className="text-hb-wood" />
              {projekt.ort}
            </div>
            <div className="flex items-center gap-2 text-sm text-hb-text">
              <Calendar size={14} className="text-hb-wood" />
              {projekt.jahr}
            </div>
            <div className="flex items-center gap-2 text-sm text-hb-text">
              <Tag size={14} className="text-hb-wood" />
              {projekt.kategorie}
            </div>
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="section-gap bg-white" data-testid="projekt-content">
        <div className="container-hb">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-16">
            <div className="lg:col-span-2 space-y-12">
              <div>
                <span className="label-text text-hb-wood block mb-4">
                  <T de="Das Projekt" it="Il Progetto" />
                </span>
                <p className="text-xl text-hb-dark font-inter leading-relaxed font-light">
                  {d('kurztext')}
                </p>
              </div>

              <div>
                <h2 className="text-sm font-mono text-hb-wood/60 tracking-widest uppercase mb-3">
                  <T de="Herausforderung" it="Sfida" />
                </h2>
                <p className="text-base text-hb-text font-inter leading-relaxed">{d('herausforderung')}</p>
              </div>

              <div>
                <h2 className="text-sm font-mono text-hb-wood/60 tracking-widest uppercase mb-3">
                  <T de="Lösung" it="Soluzione" />
                </h2>
                <p className="text-base text-hb-text font-inter leading-relaxed">{d('loesung')}</p>
              </div>

              <div>
                <h2 className="text-sm font-mono text-hb-wood/60 tracking-widest uppercase mb-3">
                  <T de="Ergebnis" it="Risultato" />
                </h2>
                <p className="text-base text-hb-text font-inter leading-relaxed">{d('ergebnis')}</p>
              </div>
            </div>

            {/* Sidebar */}
            <div className="space-y-8">
              <div className="bg-hb-off-white p-8">
                <h3 className="text-xs tracking-widest uppercase text-hb-muted font-semibold mb-5">
                  <T de="Erbrachte Leistungen" it="Servizi prestati" />
                </h3>
                <ul className="space-y-3">
                  {d('leistungen').map((l, i) => (
                    <li key={i} className="flex items-center gap-3 text-sm text-hb-text font-inter">
                      <div className="w-1.5 h-1.5 rounded-full bg-hb-wood flex-shrink-0" />
                      {l}
                    </li>
                  ))}
                </ul>
              </div>

              <div className="border border-hb-border p-8">
                <h3 className="text-sm font-manrope font-bold text-hb-dark mb-3">
                  <T de="Ähnliches Projekt geplant?" it="Avete un progetto simile?" />
                </h3>
                <p className="text-sm text-hb-text font-inter mb-5">
                  <T de="Wir beraten Sie gerne – kostenlos und unverbindlich."
                     it="Siamo lieti di consigliarvi – gratuitamente e senza impegno." />
                </p>
                <Link
                  to="/kontakt"
                  data-testid="projekt-cta-btn"
                  className="btn-primary text-xs w-full text-center"
                >
                  <T de="Anfrage stellen" it="Richiedi un preventivo" />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Image gallery */}
      {projekt.images.length > 1 && (
        <section className="section-gap bg-hb-off-white" data-testid="projekt-gallery">
          <div className="container-hb">
            <span className="label-text text-hb-wood block mb-8">
              <T de="Galerie" it="Galleria" />
            </span>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-hb-border">
              {projekt.images.map((img, i) => (
                <div key={i} className="aspect-[4/3] overflow-hidden img-zoom">
                  <img
                    src={img}
                    alt={`${d('title')} – ${i + 1}`}
                    className="w-full h-full object-cover"
                  />
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="py-20 bg-hb-dark">
        <div className="container-hb text-center">
          <h2 className="text-3xl font-manrope font-extrabold text-white mb-4">
            <T de="Haben Sie ein ähnliches Vorhaben?" it="Avete un progetto simile?" />
          </h2>
          <p className="text-white/60 font-inter mb-8">
            <T de="Wir freuen uns auf Ihre Anfrage." it="Siamo lieti di ricevere la vostra richiesta." />
          </p>
          <Link
            to="/kontakt"
            data-testid="projekt-final-cta"
            className="btn-primary bg-white text-hb-dark hover:bg-hb-off-white"
          >
            <T de="Jetzt anfragen" it="Richiedi ora" />
          </Link>
        </div>
      </section>
    </div>
  );
}
